<script lang="ts" setup>
    import type { Schemas } from '#shopware';

    /**
     * `paymentMethods` is everything to render, `availablePaymentMethods` the
     * subset that may be selected — see `useCheckoutPaymentMethod`. Methods that
     * are rendered but unavailable are shown disabled.
     *
     * The radio state comes from `v-model` alone. An additional `:checked` binding
     * used to override it, which made the rendered selection diverge from the
     * Shopware session context that the order is actually created from.
     */
    const { paymentMethods, availablePaymentMethods, isChangingPaymentMethod } = defineProps<{
        paymentMethods: Schemas['PaymentMethod'][];
        availablePaymentMethods: Schemas['PaymentMethod'][];
        /** Frozen while a change is in flight, so a second click cannot overlap it. */
        isChangingPaymentMethod: boolean;
    }>();
    const selectedPaymentMethod = defineModel<string>('selectedPaymentMethod');

    const availableIds = computed(() => new Set(availablePaymentMethods.map((method) => method.id)));
    const isAvailable = (paymentMethod: Schemas['PaymentMethod']) => availableIds.value.has(paymentMethod.id);
</script>
<template>
    <fieldset class="info-element --payment-methods">
        <div class="title">
            <legend>
                <h2>
                    {{ $t('checkout.paymentMethodLabel') }}
                </h2>
            </legend>
            <div>
                {{ $t('checkout.selectPaymentMethod') }}
            </div>
        </div>

        <div
            v-for="singlePaymentMethod in paymentMethods"
            :key="singlePaymentMethod.id"
            class="radio-container payment-method"
            :class="{ '--disabled': !isAvailable(singlePaymentMethod) }"
            data-testid="checkout-payment-method"
        >
            <input
                :id="singlePaymentMethod.id"
                v-model="selectedPaymentMethod"
                :value="singlePaymentMethod.id"
                name="payment-method"
                type="radio"
                :disabled="!isAvailable(singlePaymentMethod) || isChangingPaymentMethod"
                :data-testid="`checkout-payment-method-${singlePaymentMethod.id}`"
            />
            <label
                :for="singlePaymentMethod.id"
                class="payment-info"
            >
                <div class="payment-info">
                    <span class="payment-name">
                        {{ singlePaymentMethod.translated?.name }}
                    </span>
                </div>
                <div v-if="singlePaymentMethod.media?.url">
                    <img
                        loading="lazy"
                        :src="singlePaymentMethod.media.url"
                        :alt="`Logo of ${singlePaymentMethod.shortName}`"
                    />
                </div>
            </label>
        </div>
    </fieldset>
</template>
